// src/stores/useAppointmentBookingStore.ts
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { useApiLoading } from '../lib/composables/useApiLoading';
import { groupSlotsIntoWeeks } from '../lib/helpers/groupSlots';
import {
  authenticateBookingLinkApi,
  bookAppointmentApi,
  fetchAppointmentApi,
  fetchBookingLinkApi,
  fetchSlotsApi,
} from '../services/appointments.service';
import type { PatientDetails } from '../types';
import type {
  AppointmentFilters,
  AppointmentSummary,
  BookingLinkType,
  FilterOption,
  MpfSlotWeek,
  PractitionerGender,
} from '../types/appointment';

const emptyFilters = (): AppointmentFilters => ({
  preferredPractitionerGenders: [],
  preferredPractitioner: null,
  preferredSite: null,
});

/** Route names, in the order the patient walks them. */
const STEP_PATIENT_DETAILS = 'appointment-booking-patient-details';
const STEP_REASON = 'appointment-booking-reason-for-appointment';
const STEP_CHOOSE = 'appointment-booking-choose-appointment';
const STEP_BOOKED = 'appointment-booking-booked-successfully';

export const useAppointmentBookingStore = defineStore('appointment-booking', () => {
  const { apiLoading, setApiLoading, setApiDone } = useApiLoading();

  const bookingLinkId = ref<string | null>(null);
  const linkType = ref<BookingLinkType | null>(null);
  const patientDetails = ref<PatientDetails | null>(null);
  const isValidPatient = ref(false);
  const reasonForAppointment = ref<string | null>(null);

  // The link's flags. They only arrive once the link is resolved, which is why
  // navigation lives here rather than in the views.
  const reasonRequired = ref(false);
  const pinnedPractitionerName = ref<string | null>(null);
  const pinnedSiteName = ref<string | null>(null);
  const hasMultipleSites = ref(false);

  /** From the link load — known before the patient authenticates. */
  const sites = ref<FilterOption[]>([]);

  // Per-week: replaced on every load, never merged, or paging would offer
  // practitioners with nothing on screen.
  const practitioners = ref<FilterOption[]>([]);

  const filters = ref<AppointmentFilters>(emptyFilters());

  const weeks = ref<MpfSlotWeek[]>([]);
  const showSlotsStartingFrom = ref<string | null>(null);
  const previousFromDate = ref<string | null>(null);
  const nextFromDate = ref<string | null>(null);

  const selectedSlotId = ref<string | null>(null);
  const appointment = ref<AppointmentSummary | null>(null);
  const bookedAppointmentId = ref<string | null>(null);

  // --- getters ---------------------------------------------------------------

  /** A link that pins a practitioner offers no filters at all. */
  const hideFilters = computed(() => pinnedPractitionerName.value !== null);
  const hasFilters = computed(() => !hideFilters.value);

  /** Restricted to one site: no "Any location" option, and nothing to choose. */
  const siteRestricted = computed(() => pinnedSiteName.value !== null);

  const filtersApplied = computed(
    () =>
      filters.value.preferredPractitionerGenders.length > 0 ||
      filters.value.preferredPractitioner !== null ||
      filters.value.preferredSite !== null,
  );

  const hasAvailability = computed(() =>
    weeks.value.some((week) => week.days.some((day) => day.slots.length > 0)),
  );

  /** Skipped steps are omitted, so Back never lands on a bounced step. */
  const steps = computed(() => [
    STEP_PATIENT_DETAILS,
    ...(reasonRequired.value ? [STEP_REASON] : []),
    STEP_CHOOSE,
    STEP_BOOKED,
  ]);

  const stepAfter = (current: string, offset: number): string | null => {
    const index = steps.value.indexOf(current);
    if (index === -1) return null;

    return steps.value[index + offset] ?? null;
  };

  const nextStep = computed(() => (current: string) => stepAfter(current, 1));
  const previousStep = computed(() => (current: string) => stepAfter(current, -1));

  // --- actions ---------------------------------------------------------------

  function setPatientDetails(details: PatientDetails) {
    patientDetails.value = details;
  }

  function setReasonForAppointment(reason: string | null) {
    reasonForAppointment.value = reason;
  }

  function selectSlot(slotId: string) {
    selectedSlotId.value = slotId;
  }

  function resetFilters() {
    filters.value = emptyFilters();
  }

  async function loadLink(id: string) {
    if (apiLoading.value) return { ok: false };

    bookingLinkId.value = id;
    setApiLoading();

    try {
      const response = await fetchBookingLinkApi(id);

      if (!response.ok || !response.data) {
        return { ok: false, errors: response.errors };
      }

      const link = response.data;
      linkType.value = link.linkType;
      reasonRequired.value = link.reasonForAppointmentRequired;
      pinnedPractitionerName.value = link.pinnedPractitionerName ?? null;
      pinnedSiteName.value = link.pinnedSiteName ?? null;
      hasMultipleSites.value = link.hasMultipleSites;
      sites.value = link.sites ?? [];

      return { ok: true };
    } finally {
      setApiDone();
    }
  }

  // Sets the session cookie `loadSlots` and `book` read the patient from —
  // neither may be called before this succeeds.
  async function authenticate(details: PatientDetails) {
    if (apiLoading.value || bookingLinkId.value === null) return { ok: false };

    setApiLoading();

    try {
      const response = await authenticateBookingLinkApi(bookingLinkId.value, details);

      isValidPatient.value = response.ok;

      if (!response.ok) {
        return { ok: false, errors: response.errors };
      }

      patientDetails.value = details;

      return { ok: true };
    } finally {
      setApiDone();
    }
  }

  async function loadSlots(fromDate: string | null = showSlotsStartingFrom.value) {
    if (bookingLinkId.value === null) return { ok: false };

    showSlotsStartingFrom.value = fromDate;
    setApiLoading();

    try {
      const response = await fetchSlotsApi(bookingLinkId.value, fromDate, filters.value);

      if (!response.ok || !response.data) {
        // Status travels back: only a 401 means the session is genuinely gone,
        // and the view sends the patient back to authenticate on that alone.
        return { ok: false, status: response.status, errors: response.errors };
      }

      // The API flattens its grouping; the picker needs weeks and days back.
      weeks.value = groupSlotsIntoWeeks(response.data.slots ?? []);
      previousFromDate.value = response.data.previousFromDate;
      nextFromDate.value = response.data.nextFromDate;
      // Replaced, not merged — the list belongs to this week's window.
      practitioners.value = response.data.practitioners ?? [];

      return { ok: true };
    } finally {
      setApiDone();
    }
  }

  /** Resets the window: paging into a window the new filter emptied looks broken. */
  async function applyFilters(next: Partial<AppointmentFilters>) {
    filters.value = { ...filters.value, ...next };
    selectedSlotId.value = null;

    return loadSlots(null);
  }

  async function setPractitionerGenders(genders: PractitionerGender[]) {
    return applyFilters({ preferredPractitionerGenders: genders });
  }

  async function clearFilters() {
    resetFilters();
    selectedSlotId.value = null;

    return loadSlots(null);
  }

  async function nextWeeks() {
    if (nextFromDate.value === null) return { ok: false };

    return loadSlots(nextFromDate.value);
  }

  async function previousWeeks() {
    if (previousFromDate.value === null) return { ok: false };

    return loadSlots(previousFromDate.value);
  }

  // The POST returns the id only; a failed summary fetch does not fail the booking.
  async function book(slotId: string = selectedSlotId.value ?? '') {
    if (apiLoading.value || bookingLinkId.value === null || !slotId) return { ok: false };

    selectedSlotId.value = slotId;
    setApiLoading();

    try {
      const response = await bookAppointmentApi(bookingLinkId.value, {
        slotId,
        reasonForAppointment: reasonForAppointment.value,
      });

      if (!response.ok || !response.data) {
        // `slot-no-longer-available` is the recoverable one: the patient stays
        // on the page and picks another, so the selection is dropped.
        selectedSlotId.value = null;
        return { ok: false, errors: response.errors };
      }

      bookedAppointmentId.value = response.data.appointmentId;
      await loadAppointment(response.data.appointmentId);

      return { ok: true };
    } finally {
      setApiDone();
    }
  }

  /** Also used by booked-successfully on a direct entry, where nothing was booked this session. */
  async function loadAppointment(appointmentId: string) {
    const response = await fetchAppointmentApi(appointmentId);

    if (!response.ok || !response.data) {
      return { ok: false, errors: response.errors };
    }

    const { startDateTime, deliveryMode, location, practitioner } = response.data;
    appointment.value = { dateTime: startDateTime, deliveryMode, location, practitioner };

    return { ok: true };
  }

  return {
    apiLoading,
    appointment,
    bookedAppointmentId,
    bookingLinkId,
    filters,
    hasMultipleSites,
    isValidPatient,
    linkType,
    patientDetails,
    pinnedPractitionerName,
    pinnedSiteName,
    practitioners,
    previousFromDate,
    nextFromDate,
    reasonForAppointment,
    reasonRequired,
    selectedSlotId,
    showSlotsStartingFrom,
    sites,
    weeks,

    // getters
    filtersApplied,
    hasAvailability,
    hasFilters,
    hideFilters,
    nextStep,
    previousStep,
    siteRestricted,
    steps,

    // actions
    applyFilters,
    authenticate,
    book,
    clearFilters,
    loadAppointment,
    loadLink,
    loadSlots,
    nextWeeks,
    previousWeeks,
    resetFilters,
    selectSlot,
    setPatientDetails,
    setPractitionerGenders,
    setReasonForAppointment,
  };
});
